import TotalCase from "../../../images/totalCase.svg";
import KgEntered from "../../../images/material_kg.svg";
import TotalOrangeCase from "../../../images/total_orange.svg";
import MaterialOrange from "../../../images/material_orange.svg";
import TotalEmployee from "../../../images/total_emp.svg";
import EmployeeUnloading from "../../../images/emp_unloading.svg";
import EmployeeWashing from "../../../images/emp_washing.svg";
import AbsentEmployee from "../../../images/emp_absent.svg";
import PalletizedCase from "../../../images/palletized_cases.svg";
import NotPalletizedCase from "../../../images/not_palletized_case.svg";
import AcceptedCase from "../../../images/accepted_cases.svg";
import RejectedCase from "../../../images/rejected_case.svg";
import NotWashed from "../../../images/not_washed.svg";
import OrangeIncrease from "../../../images/orange_increase.svg";
import GreenRejected from "../../../images/green_cases_rejected.svg";
import { useMemo, useState } from "react";
import {
  useGetAllWorkingAreasQuery,
  useGetReceivingEmployeeSummaryQuery,
  useGetSummaryQuery,
  useGetUnloadingSummaryQuery,
  useGetWashingAreaSummaryQuery,
  useGetRejectionLogSummaryQuery,
} from "../../../app/globalApi";
import AddEmployeeModal from "../../../components/shared/modalContent/AddEmployeeModal";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import Button from "../../../components/shared/buttons/button";
import GridCard from "../../../components/shared/gridCard/GridCard";
import DateSelector from "../../../components/shared/fields/DateSelector";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import { ReceivingCharts } from "./ReceivingCharts";
import { EditSvg } from "../../../svgFiles/EditSvg";
import { skipToken } from "@reduxjs/toolkit/query";
import GridCardNew from "../../../components/shared/gridCard/GridCardNew";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import DashboardSection from "../../../features/dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import "./ReceivingDashboard.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { TruckIconSvg } from "../../../svgFiles/TruckIconSvg";
import { WashIconSvg } from "../../../svgFiles/WashIconSvg";
import { MapGridIconSvg } from "../../../svgFiles/MapGridIconSvg";
import { OrangeIconSvg } from "../../../svgFiles/OrangeIconSvg";
import { RejectionIconSvg } from "../../../svgFiles/RejectionIconSvg";

const ReceivingDashboard = () => {
  const { _t } = useTranslator();  
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [tab, setTab] = useState("dashboard");

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  // Receiving Summary Query
  const { data: summaryData } = useGetSummaryQuery({ tab, date });
  
  // Unloading Summary Query
  const { data: unloadingSummaryData } = useGetUnloadingSummaryQuery({ tab: "unloading", date });
  
  // Washing Area Summary Query
  const { data: washingAreaSummaryData } = useGetWashingAreaSummaryQuery({ tab: "washing", date });
  
  // Rejection Log Summary Query
  const { data: rejectionLogSummaryData } = useGetRejectionLogSummaryQuery({ tab: "rejection", date });
  
  const { data: workingAreasData, isLoading: isWorkingAreasLoading } =
    useGetAllWorkingAreasQuery();

  const receivingAreaId = useMemo(() => {
    if (!workingAreasData?.data) return null;
    const receivingArea = workingAreasData.data.find(
      (area) => area.name === "Receiving Area"
    );
    return receivingArea ? receivingArea.id : null;
  }, [workingAreasData]);

  // Receiving Overview Items - Grid Box Style
  const receivingListItems = [
    {
      label: _t("total_cases_entered", "Total Cases Entered"),
      value: summaryData?.data.total_cases_entered,
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
    },
    {
      label: _t("material_entered_kg", "Material Entered / KG"),
      value: summaryData?.data.material_entered_kg,
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
      unit: " KG",
    },
    {
      label: _t("total_orange_cases", "Total Orange Cases"),
      value: summaryData?.data.total_orange_cases,
      bgColor: "#fef3c7",
      textColor: "#fb923c",
    },
    {
      label: _t("weight_in_orange", "Weight in Orange Cases"),
      value: summaryData?.data.weight_in_orange_cases,
      bgColor: "#fef3c7",
      textColor: "#fb923c",
      unit: " KG",
    },
  ];
  
  // Unloading Overview Items - Grid Box Style
  const unloadingListItems = [
    {
      label: _t("total_cases", "Total Cases Entered"),
      value: unloadingSummaryData?.data.total_cases_entered,
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
    },
    {
      label: _t("material_kg", "Material Entered / KG"),
      value: unloadingSummaryData?.data.material_entered_kg,
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
      unit: " KG",
    },
    {
      label: _t("palletized_cases", "Palletized Cases"),
      value: unloadingSummaryData?.data.palletized_cases,
      bgColor: "#f0fdf4",
      textColor: "#4ade80",
    },
    {
      label: _t("not_palletized_cases", "Not Palletized Cases"),
      value: unloadingSummaryData?.data.non_palletized_cases,
      bgColor: "#fef3c7",
      textColor: "#fb923c",
    },
  ];

  // Washing Area Overview Items - Zoned Layout Style
  const washingAreaListItems = [
    {
      label: _t("total_cases_entered", "Total Cases Entered"),
      value: washingAreaSummaryData?.data.total_cases_entered,
    },
    {
      label: _t("total_accepted_cases", "Total Accepted Cases"),
      value: washingAreaSummaryData?.data.total_accepted_cases,
      color: "#4ade80",
    },
    {
      label: _t("rejected_cases", "Rejected Cases"),
      value: washingAreaSummaryData?.data.rejected_cases,
      color: "#f87171",
    },
    {
      label: _t("cases_not_washed", "Cases Not Washed"),
      value: washingAreaSummaryData?.data.cases_not_washed,
    },
    {
      label: _t("total_orange_cases", "Total Orange Cases"),
      value: washingAreaSummaryData?.data.total_orange_cases,
    },
    {
      label: _t("orange_case_increase", "Orange Case Increase"),
      value: washingAreaSummaryData?.data.orange_case_increase
        ? Number(washingAreaSummaryData?.data.orange_case_increase).toFixed(2) + " %"
        : "0.0 %",
    },
    {
      label: _t("weight_in_orange_case", "Weight In Orange Case"),
      value: washingAreaSummaryData?.data.weight_in_orange_cases,
      unit: " KG",
    },
  ];

  // Rejection Log Overview Items - Grid Box Style
  const rejectionLogListItems = [
    {
      label: _t("total_cases_entered", "Total Cases Entered"),
      value: rejectionLogSummaryData?.data.total_cases_entered,
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
    },
    {
      label: _t("material_entered_kg", "Material Entered / KG"),
      value: rejectionLogSummaryData?.data.material_entered_kg,
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
      unit: " KG",
    },
    {
      label: _t("green_cases_rejected", "Green Cases Rejected"),
      value: rejectionLogSummaryData?.data.green_cases_rejected,
      bgColor: "#f0fdf4",
      textColor: "#4ade80",
    },
    {
      label: _t("material_rejected_kg", "Material Rejected / KG"),
      value: rejectionLogSummaryData?.data.material_rejected_kg,
      bgColor: "#eff6ff",
      textColor: "#60a5fa",
      unit: " KG",
    },
    {
      label: _t("paid_count", "Paid / Count"),
      value: rejectionLogSummaryData?.data.paid_count,
      bgColor: "#f0fdf4",
      textColor: "#4ade80",
    },
    {
      label: _t("paid_kg", "Paid / KG"),
      value: rejectionLogSummaryData?.data.paid_kg,
      bgColor: "#f0fdf4",
      textColor: "#4ade80",
      unit: " KG",
    },
    {
      label: _t("unpaid_count", "Unpaid / Count"),
      value: rejectionLogSummaryData?.data.unpaid_count,
      bgColor: "#fef2f0",
      textColor: "#f87171",
    },
    {
      label: _t("unpaid_kg", "Unpaid / KG"),
      value: rejectionLogSummaryData?.data.unpaid_kg,
      bgColor: "#fef2f0",
      textColor: "#f87171",
      unit: " KG",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="receiving_dashboard_toolbar align-items-center mb-4">
        <h5 className="receiving_dashboard_toolbar_title m-0">
          {_t("receiving_overview", "Receiving Overview")}
        </h5>
        <div className="receiving_filter_group">          
          <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="receiving-date-range-filter" />          
        </div>
      </div>

      <div className="d-flex flex-column gap-4">
        {/* ── Receiving Overview: Grid Box Style ────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><PackageIconSvg size={18} color="#60a5fa" />{_t("receiving_data_title", "Receiving Data")}</span>}>
          <div className="pp-brokering-grid">
            {receivingListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box">
                <div className="pp-brokering-box-label" style={{ color: item.textColor }}>{item.label}</div>
                <div className="pp-brokering-box-value" style={{ color: item.textColor }}>{item.value}{item.unit || ''}</div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* ── Unloading Overview: Similar to FruitQualityMetrics ───── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><TruckIconSvg size={18} color="#60a5fa" />{_t("unloading_overview", "Unloading Overview")}</span>}>
          <div className="fqm-grid">
            {unloadingListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box fqm-box">
                <div className="pp-brokering-box-label" style={{ whiteSpace: "pre-line" }}>{item.label}</div>
                <div className="pp-brokering-box-value">{item.value}{item.unit || ''}</div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* ── Washing Area Overview: Zoned Layout Style ──────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><WashIconSvg size={18} color="#60a5fa" />{_t("washing_area_overview", "Washing Area Overview")}</span>}>
          <div className="db-avg-card">
            {/* Zone 1: Main Metrics */}
            <div className="db-avg-header">
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <WashIconSvg size={15} color="#60a5fa" />{_t("washing_metrics", "Washing Metrics")}
              </span>
            </div>
            <div className="db-trash-grid">
              {washingAreaListItems.slice(0, 4).map((item, i) => (
                <div key={i} className="db-trash-cell">
                  <span>{item.label}</span>
                  <span className="db-trash-val" style={item.color ? { color: item.color } : {}}>
                    {item.value}{item.unit || ''}
                  </span>
                </div>
              ))}
            </div>

           

            {/* Zone 3: Washing Areas Distribution */}
            <div className="db-avg-header" style={{ borderTop: "1px solid #f0f1f3" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <MapGridIconSvg size={15} color="#60a5fa" />{_t("washing_areas_distribution", "Washing Areas Distribution")}
              </span>
            </div>
            <div className="pp-brokering-grid" style={{ padding: "16px 18px" }}>
              {washingAreaSummaryData?.data?.washing_areas_data &&
                Object.entries(washingAreaSummaryData.data.washing_areas_data).map(([areaName, value], i) => {
                  const isRoom = /^room/i.test(areaName);
                  const isColdStorage = /cold storage/i.test(areaName);
                  const bgColor = isColdStorage ? "#eff6ff" : isRoom ? "#fef3c7" : "#f0fdf4";
                  const textColor = isColdStorage ? "#60a5fa" : isRoom ? "#fb923c" : "#4ade80";
                  return (
                    <div key={i} className="pp-brokering-box">
                      <div className="pp-brokering-box-label" style={{ color: textColor }}>{areaName}</div>
                      <div className="pp-brokering-box-value" style={{ color: textColor }}>{value ?? "0"} KG</div>
                    </div>
                  );
                })
              }
            </div>
             {/* Zone 2: Orange Cases Metrics */}
            <div className="db-avg-header" style={{ borderTop: "1px solid #f0f1f3" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <OrangeIconSvg size={15} color="#fb923c" />{_t("orange_metrics", "Orange Cases Metrics")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              {washingAreaListItems.slice(4, 7).map((item, i) => (
                <div key={i} className="db-inner-metric">
                  <div className="db-metric-label">{item.label}</div>
                  <div className="db-metric-value">{item.value}{item.unit || ''}</div>
                </div>
              ))}
            </div>
          </div>
        </DashboardSection>

        {/* ── Rejection Log Overview: Grid Box Style ───────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><RejectionIconSvg size={18} color="#f87171" />{_t("rejection_log_overview", "Rejection Log Overview")}</span>}>
          <div className="pp-brokering-grid">
            {rejectionLogListItems.map((item, i) => (
              <div key={i} className="pp-brokering-box">
                <div className="pp-brokering-box-label" style={{ color: item.textColor }}>{item.label}</div>
                <div className="pp-brokering-box-value" style={{ color: item.textColor }}>{item.value}{item.unit || ''}</div>
              </div>
            ))}
          </div>
        </DashboardSection>

        <ReceivingCharts startDate={startDate} endDate={endDate} />
      </div>
    </div>
  );
};

export default ReceivingDashboard;